import React, { useState, useEffect, useCallback } from 'react';
import { ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaTrash, FaPlus, FaHistory, FaTimes } from 'react-icons/fa';
import ListsManager from '../services/ListsManager';
import AnalyticsService from '../services/AnalyticsService';
import { useTheme } from '../theme/ThemeContext';

const ACTION_TYPE_VALUES = [
    { value: 'react',               key: 'actionReact' },
    { value: 'counter_narrative',   key: 'actionCounter' },
    { value: 'amplify',             key: 'actionAmplify' },
    { value: 'share',               key: 'actionShare' },
    { value: 'report',              key: 'actionReport' },
    { value: 'monitor',             key: 'actionMonitor' },
    { value: 'respond_to_comments', key: 'actionRespond' },
    { value: 'boost_post',          key: 'actionBoost' },
    { value: 'adjust_strategy',     key: 'actionAdjust' },
];

const EFFECTIVENESS_VALUES = [
    { value: 1, emoji: '😞', key: 'effPoor' },
    { value: 2, emoji: '😐', key: 'effMeh' },
    { value: 3, emoji: '🤷', key: 'effOK' },
    { value: 4, emoji: '😊', key: 'effGood' },
    { value: 5, emoji: '🎉', key: 'effGreat' },
];

let T = {};

// ── Shared helpers ────────────────────────────────────────────────────────────

const Label = ({ children }) => (
    <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>
        {children}
    </div>
);

const Field = ({ label, children }) => (
    <div style={{ marginBottom: 16 }}>
        <Label>{label}</Label>
        {children}
    </div>
);

const textareaStyle = () => ({
    width: '100%', boxSizing: 'border-box', resize: 'none',
    background: T.surface, border: `1px solid ${T.border}`,
    borderRadius: 10, padding: '10px 12px', color: T.text,
    fontSize: 13, lineHeight: '19px', outline: 'none', fontFamily: 'inherit',
});

// ── Add form ──────────────────────────────────────────────────────────────────

const AddForm = ({ onSave, onCancel, saving }) => {
    const { t } = useTranslation();
    const ACTION_TYPES = ACTION_TYPE_VALUES.map(at => ({ ...at, label: t(`app.pastCases.${at.key}`) }));
    const EFFECTIVENESS = EFFECTIVENESS_VALUES.map(e => ({ ...e, label: t(`app.pastCases.${e.key}`) }));

    const [title, setTitle]         = useState('');
    const [situation, setSituation] = useState('');
    const [execution, setExecution] = useState('');
    const [action, setAction]       = useState('react');
    const [eff, setEff]             = useState(3);
    const [outcome, setOutcome]     = useState('');
    const valid = title.trim().length >= 5;

    return (
        <div style={{ background: T.card, borderRadius: 16, border: `1px solid ${T.border}`, padding: '20px 20px 16px', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <span style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>{t('app.pastCases.newPastAction')}</span>
                <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                    <FaTimes size={13} color={T.textMuted} />
                </button>
            </div>

            <Field label={t('app.pastCases.whatHappened')}>
                <textarea
                    rows={2}
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="e.g. Responded to a safety controversy on Twitter"
                    style={textareaStyle()}
                />
            </Field>

            <Field label={t('app.pastCases.situation')}>
                <textarea
                    rows={2}
                    value={situation}
                    onChange={e => setSituation(e.target.value)}
                    placeholder="What triggered this? What was the context?"
                    style={textareaStyle()}
                />
            </Field>

            <Field label={t('app.pastCases.stepsTaken')}>
                <textarea
                    rows={2}
                    value={execution}
                    onChange={e => setExecution(e.target.value)}
                    placeholder="e.g. Posted an apology, sent DMs, escalated internally"
                    style={textareaStyle()}
                />
            </Field>

            <Field label={t('app.pastCases.actionType')}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {ACTION_TYPES.map(at => {
                        const active = action === at.value;
                        return (
                            <button key={at.value} onClick={() => setAction(at.value)} style={{
                                padding: '4px 11px', borderRadius: 20, cursor: 'pointer', fontSize: 11, fontWeight: active ? 700 : 500,
                                background: active ? `${T.primary}20` : T.surface,
                                border: `1px solid ${active ? T.primary : T.border}`,
                                color: active ? T.primary : T.textSec,
                            }}>{at.label}</button>
                        );
                    })}
                </div>
            </Field>

            <Field label={t('app.pastCases.effectiveness')}>
                <div style={{ display: 'flex', gap: 6 }}>
                    {EFFECTIVENESS.map(e => {
                        const active = eff === e.value;
                        return (
                            <button key={e.value} onClick={() => setEff(e.value)} style={{
                                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                                padding: '8px 4px', borderRadius: 10, cursor: 'pointer',
                                background: active ? `${T.primary}18` : T.surface,
                                border: `1px solid ${active ? T.primary : T.border}`,
                            }}>
                                <span style={{ fontSize: 20, marginBottom: 3 }}>{e.emoji}</span>
                                <span style={{ color: active ? T.primary : T.textMuted, fontSize: 9, fontWeight: active ? 700 : 500 }}>{e.label}</span>
                            </button>
                        );
                    })}
                </div>
            </Field>

            <Field label={t('app.pastCases.results')}>
                <textarea
                    rows={2}
                    value={outcome}
                    onChange={e => setOutcome(e.target.value)}
                    placeholder="e.g. Sentiment improved 20% within 48h"
                    style={textareaStyle()}
                />
            </Field>

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button onClick={onCancel} style={{
                    flex: 1, padding: '10px 0', borderRadius: 10, border: `1px solid ${T.border}`,
                    background: T.surface, color: T.textSec, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>{t('app.pastCases.cancel')}</button>
                <button onClick={() => onSave({ title: title.trim(), situation: situation.trim(), action, execution: execution.trim(), eff, outcome: outcome.trim() })}
                    disabled={saving || !valid}
                    style={{
                        flex: 2, padding: '10px 0', borderRadius: 10, border: 'none',
                        background: valid ? T.primary : `${T.primary}35`,
                        color: '#fff', fontSize: 12, fontWeight: 700,
                        cursor: valid ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                    {saving ? <ActivityIndicator size="small" color="#fff" /> : t('app.pastCases.saveAction')}
                </button>
            </div>
        </div>
    );
};

// ── Case card ─────────────────────────────────────────────────────────────────

const CaseCard = ({ c, onDelete }) => {
    const { t } = useTranslation();
    const EFFECTIVENESS = EFFECTIVENESS_VALUES.map(e => ({ ...e, label: t(`app.pastCases.${e.key}`) }));
    const effObj = EFFECTIVENESS.find(e => e.value === c.effectiveness);
    return (
        <div style={{
            background: T.card, borderRadius: 14, border: `1px solid ${T.border}`,
            padding: '14px 16px', marginBottom: 8,
            borderLeft: `3px solid ${T.primary}`,
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: T.text, fontSize: 13, fontWeight: 700, marginBottom: 8, lineHeight: '18px' }}>
                        {c.title}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: c.situation_description || c.execution_details || c.outcome_notes ? 10 : 0 }}>
                        <span style={{
                            background: `${T.primary}18`, border: `1px solid ${T.primary}30`,
                            padding: '2px 8px', borderRadius: 5,
                            color: T.primary, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                        }}>{c.action_taken?.replace(/_/g, ' ')}</span>
                        {effObj && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ fontSize: 13 }}>{effObj.emoji}</span>
                                <span style={{ color: T.textMuted, fontSize: 10 }}>{effObj.label}</span>
                            </span>
                        )}
                        <span style={{
                            color: T.textMuted, fontSize: 10,
                            background: T.surface, padding: '2px 7px', borderRadius: 4,
                        }}>{c.source === 'action_queue' ? 'AI Queue' : 'Manual'}</span>
                    </div>
                    {(c.situation_description || c.execution_details || c.outcome_notes) && (
                        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {c.situation_description && (
                                <div style={{ color: T.textSec, fontSize: 11, lineHeight: '16px' }}>
                                    <span style={{ color: T.textMuted, fontWeight: 700 }}>{t('app.pastCases.situationLabel')}</span>{c.situation_description}
                                </div>
                            )}
                            {c.execution_details && (
                                <div style={{ color: T.textSec, fontSize: 11, lineHeight: '16px' }}>
                                    <span style={{ color: T.textMuted, fontWeight: 700 }}>{t('app.pastCases.stepsLabel')}</span>{c.execution_details}
                                </div>
                            )}
                            {c.outcome_notes && (
                                <div style={{ color: T.textSec, fontSize: 11, lineHeight: '16px', fontStyle: 'italic' }}>
                                    <span style={{ color: T.textMuted, fontWeight: 700, fontStyle: 'normal' }}>{t('app.pastCases.resultLabel')}</span>{c.outcome_notes}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <button onClick={() => onDelete(c.id)} style={{
                    padding: '6px 6px', borderRadius: 8, background: 'transparent',
                    border: 'none', cursor: 'pointer', flexShrink: 0, opacity: 0.5,
                }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0.5}
                >
                    <FaTrash size={11} color={T.textMuted} />
                </button>
            </div>
        </div>
    );
};

// ── Main screen ───────────────────────────────────────────────────────────────

const PastCasesScreen = ({ onBack } = {}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { theme } = useTheme();

    T = {
        bg:        theme.bg,
        card:      theme.bgCard,
        surface:   theme.bgSurface,
        border:    theme.border,
        text:      theme.textPrimary,
        textSec:   theme.textSecondary,
        textMuted: theme.textMuted,
        primary:   '#0a84ff',
        success:   '#30d158',
    };

    const [cases, setCases]       = useState([]);
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);
    const [showForm, setShowForm] = useState(false);

    const embedded = !!onBack;

    const fetchCases = useCallback(async () => {
        const result = await ListsManager.fetchPastCases();
        if (result?.cases) setCases(result.cases);
        setLoading(false);
    }, []);

    useEffect(() => { fetchCases(); AnalyticsService.logActivity('view_past_cases'); }, [fetchCases]);

    const handleSave = async ({ title, situation, action, execution, eff, outcome }) => {
        setSaving(true);
        const result = await ListsManager.createPastCase(title, situation, action, execution, eff, outcome, 'all');
        if (result) { setShowForm(false); await fetchCases(); }
        setSaving(false);
    };

    const handleDelete = async (id) => {
        await ListsManager.deletePastCase(id);
        setCases(prev => prev.filter(c => c.id !== id));
    };

    const css = `
        .pc-add-btn:hover { filter: brightness(1.1); }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.2); border-radius: 2px; }
    `;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: css }} />
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg, overflow: 'hidden' }}>

                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: embedded ? '18px 24px 14px' : '14px 24px',
                    borderBottom: `1px solid ${T.border}`, flexShrink: 0,
                }}>
                    {!embedded && (
                        <button onClick={() => navigate(-1)} style={{ width: 34, height: 34, borderRadius: 10, background: T.surface, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                            <FaArrowLeft size={13} color={T.text} />
                        </button>
                    )}
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <FaHistory size={14} color="#f59e0b" />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ color: T.text, fontSize: embedded ? 15 : 18, fontWeight: 700, letterSpacing: -0.3 }}>{t('app.pastCases.title')}</div>
                        <div style={{ color: T.textMuted, fontSize: 11, marginTop: 1 }}>
                            {loading ? t('app.pastCases.loading') : (cases.length !== 1 ? t('app.pastCases.actionsLogged', { count: cases.length }) : t('app.pastCases.actionLogged', { count: cases.length }))}
                        </div>
                    </div>
                    <button
                        className="pc-add-btn"
                        onClick={() => setShowForm(f => !f)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                            background: showForm ? T.surface : T.primary,
                            transition: 'background 0.15s',
                        }}
                    >
                        {showForm
                            ? <FaTimes size={11} color={T.textMuted} />
                            : <FaPlus size={10} color="#fff" />}
                        <span style={{ color: showForm ? T.textSec : '#fff', fontSize: 12, fontWeight: 600 }}>
                            {showForm ? t('app.pastCases.cancel') : t('app.pastCases.add')}
                        </span>
                    </button>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: embedded ? '16px 24px 28px' : '16px 24px 28px' }}>

                    {/* Intro blurb */}
                    {!showForm && cases.length === 0 && !loading && (
                        <div style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 14, padding: '16px 18px', marginBottom: 20 }}>
                            <div style={{ color: '#f59e0b', fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{t('app.pastCases.trainAI')}</div>
                            <div style={{ color: T.textSec, fontSize: 12, lineHeight: '18px' }}>
                                {t('app.pastCases.trainAIDesc')}
                            </div>
                        </div>
                    )}

                    {showForm && (
                        <AddForm onSave={handleSave} onCancel={() => setShowForm(false)} saving={saving} />
                    )}

                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 48 }}>
                            <ActivityIndicator size="large" color={T.primary} />
                        </div>
                    ) : cases.length === 0 && !showForm ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 40, gap: 10 }}>
                            <div style={{ width: 48, height: 48, borderRadius: 16, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FaHistory size={20} color="#f59e0b" />
                            </div>
                            <span style={{ color: T.textMuted, fontSize: 13, textAlign: 'center' }}>{t('app.pastCases.noPastActions').split('\n').map((line, i) => <span key={i}>{line}{i === 0 && <br />}</span>)}</span>
                        </div>
                    ) : cases.length > 0 && (
                        <>
                            <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
                                {cases.length !== 1 ? t('app.pastCases.loggedActionsPlural', { count: cases.length }) : t('app.pastCases.loggedActions', { count: cases.length })}
                            </div>
                            {cases.map(c => <CaseCard key={c.id} c={c} onDelete={handleDelete} />)}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default PastCasesScreen;
